import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mt-6 text-2xl font-semibold tracking-tight text-slate-900 first:mt-0 dark:text-white">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-6 text-xl font-semibold tracking-tight text-slate-900 first:mt-0 dark:text-white">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-5 text-lg font-semibold text-slate-900 first:mt-0 dark:text-white">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-4 text-base font-semibold text-slate-900 first:mt-0 dark:text-white">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="mt-3 text-sm leading-relaxed text-slate-700 first:mt-0 dark:text-zinc-200">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700 dark:text-zinc-200">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700 dark:text-zinc-200">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noreferrer" : undefined}
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="mt-3 border-l-2 border-slate-300 pl-4 text-sm text-slate-600 dark:border-white/20 dark:text-zinc-300">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-slate-200 dark:border-white/10" />,
  code: ({ className, children }) => {
    const isBlock = Boolean(className?.includes("language-"));
    if (isBlock) {
      return (
        <code className="block overflow-x-auto rounded-md bg-slate-950/95 p-3 font-mono text-xs leading-relaxed text-slate-100 dark:bg-black/50">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800 dark:bg-white/10 dark:text-zinc-100">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="mt-3 overflow-x-auto">{children}</pre>,
  table: ({ children }) => (
    <div className="mt-3 overflow-x-auto">
      <table className="min-w-full border-collapse text-left text-sm text-slate-700 dark:text-zinc-200">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-slate-200 dark:border-white/10">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-t border-slate-100 px-3 py-2 dark:border-white/5">{children}</td>
  ),
};

/** Renders article markdown for the read-only knowledge view. */
export function MarkdownArticleBody({ markdown }: { markdown: string }) {
  const body = markdown.trim();
  if (!body) {
    return <p className="mt-2 text-sm text-slate-500 dark:text-zinc-500">No body yet.</p>;
  }

  return (
    <div className="mt-2 max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {body}
      </ReactMarkdown>
    </div>
  );
}
